import { useRouteModalHistory } from '@/router'
import { Icon, State, Tooltip } from '@seiue/ui'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import { $t } from 'packages/locale'
import { FC, Fragment, useContext } from 'react'
import styled, { useTheme } from 'styled-components'

import { Flex, FlexBaseline, H1 } from '../../components/Styled'
import { DataBoardContext } from '../context'
import { DataBoardCardType } from '../types'

/**
 * 首页卡片
 *
 * @param param0 - 参数
 * @param param0.title - 标题
 * @param param0.count - 数量
 * @param param0.items - 卡片项
 * @param param0.type - 类型
 * @param param0.loading - 是否加载中
 * @param param0.flex - flex
 * @param param0.onTitleClick - 标题点击回调
 * @param param0.tips - 提示
 * @param param0.style - 样式
 * @param param0.itemIconMsg - 图标消息
 * @returns 首页卡片
 */
export const IndexBoardCard: FC<DataBoardCardType> = ({
  title,
  count,
  items,
  flex,
  type,
  loading,
  onTitleClick,
  tips,
  style,
  itemIconMsg,
}) => {
  const modalHistory = useRouteModalHistory()
  const { timeRange } = useContext(DataBoardContext)
  const theme = useTheme()

  return (
    <Wrapper
      $flex={flex}
      style={style}
    >
      {loading ? (
        <State.Loading style={{ minHeight: 165 }} />
      ) : (
        <>
          <Main $hasBottom={!!items.length}>
            <FlexWrapper
              style={{
                cursor: 'pointer',
                justifyContent: 'space-between',
              }}
              onClick={() => {
                if (onTitleClick) {
                  onTitleClick()
                  return
                }

                if (type) {
                  modalHistory.push('GatewayStatsDetails', {
                    type,
                    timeRange,
                  })
                }
              }}
            >
              <FlexWrapper>
                {tips ? (
                  <FlexWrapper>
                    <Title>{title}</Title>
                    <Tooltip
                      title={tips}
                      style={{ maxWidth: 237 }}
                    >
                      <Icon
                        icon="Help"
                        size={16}
                        color="#455766"
                        style={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                  </FlexWrapper>
                ) : (
                  <Title>{title}</Title>
                )}
              </FlexWrapper>
              <FlexWrapper>
                <TipLabel>{$t('详情')}</TipLabel>
                <GrowpIcon
                  name="Left"
                  size={12}
                  color={theme.text._3}
                />
              </FlexWrapper>
            </FlexWrapper>

            <Flex
              $gap={12}
              $align="center"
            >
              <IconWrap $bgColor={itemIconMsg.backgroundColor}>
                <GrowpIcon
                  name={itemIconMsg.name}
                  size={16}
                  color={itemIconMsg.color}
                />
              </IconWrap>
              <FlexBaseline style={{ gap: 2 }}>
                <MainCount>{count}</MainCount>
                <Label>{$t('人')}</Label>
              </FlexBaseline>
            </Flex>
          </Main>
          {!!items.length && (
            <BottomRow>
              {items.map((item, index) => (
                <Fragment key={index}>
                  <BottomItem>
                    <Label>{item.label}</Label>
                    <FlexBaseline style={{ gap: 2 }}>
                      <BottomValue>{item.value}</BottomValue>
                      <Label>{item.unit}</Label>
                    </FlexBaseline>
                  </BottomItem>
                  {index !== items.length - 1 && <VerticalDivider />}
                </Fragment>
              ))}
            </BottomRow>
          )}
        </>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div<{ $flex?: number }>`
  background: #ffffff;
  border-radius: 8px;
  flex: ${({ $flex }) => $flex};
  min-height: 164px;
`

const FlexWrapper = styled(Flex)`
  gap: 4px;
`

const Main = styled.div<{ $hasBottom: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: ${({ $hasBottom, theme }) =>
    $hasBottom ? `1px solid ${theme.border.greyLight}` : 'none'};
`

const Title = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${({ theme }) => theme.text._1};
`

const TipLabel = styled.div`
  font-size: 12px;
  line-height: 20px;
  color: ${({ theme }) => theme.text._3};
`

const IconWrap = styled.div<{ $bgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: ${({ $bgColor }) => $bgColor};
`

const MainCount = styled(H1)`
  font-size: 28px;
  font-weight: bold;
  line-height: 32px;
  color: ${({ theme }) => theme.text._1};
`

const BottomRow = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  padding: 12px 16px;
`

const BottomItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  align-items: center;
`

const VerticalDivider = styled.div`
  width: 1px;
  background: ${({ theme }) => theme.border?.greyNormal || '#e5e6eb'};
  flex-shrink: 0;
`

const BottomValue = styled.div`
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
  color: ${({ theme }) => theme.text._1};
`

const Label = styled.div`
  font-size: 12px;
  line-height: 20px;
  color: ${({ theme }) => theme.text._2};
`
